import type { ThreeEvent } from '@react-three/fiber';
import { Plane, Raycaster, Vector2, Vector3 } from 'three';
import type {
  Camera,
  Intersection,
  Object3D,
  Object3DEventMap,
  Vector3Like,
  Vector3Tuple,
  WebGLRenderer,
} from 'three';

const AXIS_UNIT_VECTOR = {
  0: Object.freeze(new Vector3(1, 0, 0)),
  1: Object.freeze(new Vector3(0, 1, 0)),
  2: Object.freeze(new Vector3(0, 0, 1)),
  get x() {
    return this[0];
  },
  get y() {
    return this[1];
  },
  get z() {
    return this[2];
  },
};

export const getPlanePoint = (params: {
  x: number;
  y: number;
  raycaster: Raycaster;
  camera: Camera;
  vector?: Vector3Tuple;
}) => {
  const { x, y, raycaster, camera, vector = [0, 0, 1] } = params;
  const plane = new Plane(new Vector3(...vector), 0);
  const point = new Vector3();

  raycaster.setFromCamera(new Vector2(x, y), camera);

  raycaster.ray.intersectPlane(plane, point);

  return point;
};

export const getVectorByAxis = (axis: keyof typeof AXIS_UNIT_VECTOR) => {
  return new Vector3(...AXIS_UNIT_VECTOR[axis].toArray());
};

export const computeMouse = <E extends ThreeEvent<MouseEvent> | MouseEvent>({
  camera,
  event,
  gl,
  targetIntersections,
}: {
  camera: Camera;
  event: E;
  gl: WebGLRenderer;
  targetIntersections: Object3D<Object3DEventMap>[];
}) => {
  const mouse = new Vector2();
  const raycaster = new Raycaster();
  const rect = gl.domElement.getBoundingClientRect();

  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  raycaster.setFromCamera(mouse, camera);

  const intersections = raycaster.intersectObjects(targetIntersections);

  Object.defineProperty(event, 'intersections', {
    configurable: true,
    get() {
      return intersections;
    },
  });

  return event as E & {
    intersections: Intersection<Object3D<Object3DEventMap>>[];
  };
};

export const subVector = (() => {
  const v = new Vector3();

  return (a: Vector3Like, b: Vector3Like) => v.subVectors(a, b);
})();
